import React from "react";
import { LegacyButton as Button } from "web-ui/legacy";

import type { TtsRequestParameters } from "../api/duolingoTts";
import {
  generateAudioWithDuolingoTts,
  getDuolingoTtsLink,
} from "../api/duolingoTts";
import { S } from "../strings";
import styles from "../styles/RequestParametersDisplay.module.scss";
import { getAnnotationLink } from "../util/annotationLinks";

import { AudioControlButtons } from "./AudioControlButtons";
import { TextWithSpacesShown } from "./TextWithSpacesShown";

export const RequestParametersDisplay = ({
  audioRef,
  ttsData,
  showAudioButton,
}: {
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  ttsData: TtsRequestParameters;
  showAudioButton: boolean;
}) => {
  const generateLabeledItem = (
    label: string,
    itemText: string | JSX.Element,
  ) => (
    <div className={styles["label-wrap"]}>
      <p className={styles["label"]}>{label}</p>
      <p className={styles["text-item"]}>{itemText}</p>
    </div>
  );

  const generateAnnotationLink = ttsData.has_speech_annotation ? (
    <a href={getAnnotationLink(ttsData.tts_hash)}>{S.LINK}</a>
  ) : (
    S.NO_SPEECH_ANNOTATION
  );

  return (
    <div className={styles["display-box"]}>
      {showAudioButton ? (
        <AudioControlButtons
          audioRef={audioRef}
          color="macaw"
          source={getDuolingoTtsLink(ttsData.voice, ttsData.tts_hash)}
          submitting={false}
          type="submit"
          variant="solid"
        />
      ) : null}
      <div className={styles["display-box"]}>
        {generateLabeledItem(
          S.TEXT,
          <TextWithSpacesShown text={ttsData.text} />,
        )}
        {generateLabeledItem(S.SPEED, ttsData.speed)}
        {generateLabeledItem(S.LANGUAGE_ID, ttsData.language_id)}
        {generateLabeledItem(S.VOICE, ttsData.voice)}
        {generateLabeledItem(S.CREATOR, ttsData.creator)}
        {generateLabeledItem(S.CREATED_DATE, ttsData.creation_date)}
        {generateLabeledItem(S.SSML, ttsData.ssml ?? S.NO_SSML)}
        {generateLabeledItem(S.HAS_SPEECH_ANNOTATION, generateAnnotationLink)}
        {generateLabeledItem(S.TTS_HASH, ttsData.tts_hash)}
        {generateLabeledItem(
          S.LANGUAGE_VOICE_TEXT_HASH,
          ttsData.language_voice_text_hash,
        )}
        {generateLabeledItem(
          S.REGENERATE_AUDIO,
          <TtsRegenerationButton ttsData={ttsData} />,
        )}
      </div>
    </div>
  );
};

/* eslint-disable react/jsx-no-literals */
const TtsRegenerationButton = ({
  ttsData,
}: {
  ttsData: TtsRequestParameters;
}) => {
  const [submissionStatus, setSubmissionStatus] = React.useState<
    "errored" | "notSubmitted" | "submitted"
  >("notSubmitted");

  switch (submissionStatus) {
    case "errored":
      return <>Something went wrong.</>;
    case "submitted":
      return (
        <>
          Your request has been submitted. This can take a few minutes,
          especially for World voices. To see the new audio, refresh the page
          and check for the new created date.
        </>
      );
    case "notSubmitted":
      return (
        <Button
          onClick={() => {
            try {
              generateAudioWithDuolingoTts(
                {
                  force: true,
                  speeds: [ttsData.speed],
                  ssml_body: { [ttsData.text]: ttsData.ssml },
                  texts: [ttsData.text],
                  voices: [ttsData.voice],
                },
                ttsData.language_id,
              );
              setSubmissionStatus("submitted");
            } catch {
              setSubmissionStatus("errored");
            }
          }}
          variant="solid"
        >
          {S.REGENERATE_AUDIO}
        </Button>
      );
    default:
      throw new Error("Invalid submission status");
  }
};
